import React, { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { Info } from "lucide-react";
import { logIcons, logColors } from "@/components/catalog/constants/logStyles";
import { formatLogTime } from "@/lib/catalog/formatLogTime";
import type { LogViewerProps } from "./LogViewer.types";

export const LogViewer: React.FC<LogViewerProps> = ({
  logs,
  maxHeight = "300px",
  className,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [logs.length]);

  return (
    <div
      data-testid="log-viewer"
      className={cn(
        "rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden",
        className,
      )}
    >
      <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Sync Log
        </h4>
      </div>
      <div
        ref={scrollContainerRef}
        className="p-4 bg-slate-950 overflow-y-auto font-mono text-sm"
        style={{ maxHeight }}
      >
        {logs.length === 0 && (
          <p className="text-slate-500 text-xs text-center py-4">
            Waiting for sync logs...
          </p>
        )}
        {logs.map((log) => {
          const level = log.level ?? "info";
          const Icon = logIcons[level] || Info;
          return (
            <div
              key={String(log.id ?? Math.random())}
              data-testid={`log-entry-${log.id}`}
              className="flex items-start gap-3 py-1.5"
            >
              <span className="text-slate-500 text-xs shrink-0">
                {formatLogTime(log.timestamp ?? "")}
              </span>
              <Icon
                className={cn("w-4 h-4 mt-0.5 shrink-0", logColors[level])}
              />
              <span
                className={cn(
                  "text-slate-300",
                  level === "error" && "text-rose-300",
                  level === "warning" && "text-amber-300",
                )}
              >
                {log.message ?? ""}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
